import * as React from "react";
import { T } from "./Translate";
import { useState, useEffect } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import PayPalSurface from "../components/PayPalSurface";
import { doc, updateDoc } from "firebase/firestore";
import { db, functions } from "../firebase";
import { getSession } from "../session";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CssBaseline from "@mui/material/CssBaseline";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Alert from "@mui/material/Alert";
import { ArrowForward as ArrowForwardIcon } from "../icons";
import { Cancel as CancelIcon } from "../icons";
import { CheckCircleOutline as CheckCircleOutlineIcon } from "../icons";
import { LocalOffer as LocalOfferIcon } from "../icons";
import { Event as EventIcon } from "../icons";
import { Flight as FlightIcon } from "../icons";
import { Lock as LockIcon } from "../icons";
import { DirectionsCar as DirectionsCarIcon } from "../icons";
import { Star as StarIcon } from "../icons";
import CircularProgress from "@mui/material/CircularProgress";
import { Container } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSubscriptionContext } from "./SubscriptionContext";
import PageHeader from "./PageHeader";
import { pageContentSx } from "../pageLayout";

// Configuración de planes
const plans = {
  mensual: [
    {
      name: "Free",
      isFreeTrial: true,
      price: 0,
      trialDays: 30,
      priceAfterTrial: 29.99,
      plan_id: null,
      features: [
        "Gratis por 30 días, sin tarjeta",
        "Luego $29.99/mes (precio del plan Básico)",
        "3 publicaciones de ofertas por mes",
        "2 publicaciones de eventos por mes",
        "1 publicación de viajes por mes",
        "1 publicación de autos por mes",
        "1 tienda online",
        "Local en Pedidos (comida o súper)",
      ],
    },
    {
      name: "Básico",
      price: 29.99,
      plan_id: "P-36717395FG221790MNJYPIVI",
      features: [
        "3 publicaciones de ofertas por mes",
        "2 publicaciones de eventos por mes",
        "1 publicación de viajes por mes",
        "1 publicación de autos por mes",
        "Local en Pedidos (comida o súper)",
        "Vista previa de publicaciones",
      ],
    },
    {
      name: "Profesional",
      price: 59.99,
      plan_id: "P-6L880889GD8125027NJYPJNI",
      features: [
        "10 publicaciones de ofertas por mes",
        "5 publicaciones de eventos por mes",
        "3 publicaciones de viajes por mes",
        "3 publicaciones de autos por mes",
        "1 oferta y 1 evento destacados por mes",
        "Local en Pedidos (comida o súper)",
        "Vista previa de publicaciones",
      ],
    },
    {
      name: "Premium",
      price: 99.99,
      plan_id: "P-4B369868XJ9631115NJYPKCI",
      features: [
        "Publicaciones ilimitadas de ofertas",
        "Publicaciones ilimitadas de eventos",
        "Publicaciones ilimitadas de viajes",
        "Publicaciones ilimitadas de autos",
        "3 ofertas y 3 eventos destacados por mes",
        "Local en Pedidos (comida o súper)",
        "Vista previa de publicaciones",
      ],
    },
  ],
  anual: [
    {
      name: "Básico",
      price: 299.99,
      plan_id: "P-7FB49596F72762034NJYPK5Q",
      features: [
        "3 publicaciones de ofertas por mes",
        "2 publicaciones de eventos por mes",
        "1 publicación de viajes por mes",
        "1 publicación de autos por mes",
        "Local en Pedidos (comida o súper)",
        "Vista previa de publicaciones",
      ],
    },
    {
      name: "Profesional",
      price: 599.99,
      plan_id: "P-7S9454979G303731UNJYPLTQ",
      features: [
        "10 publicaciones de ofertas por mes",
        "5 publicaciones de eventos por mes",
        "3 publicaciones de viajes por mes",
        "3 publicaciones de autos por mes",
        "1 oferta y 1 evento destacados por mes",
        "Local en Pedidos (comida o súper)",
        "Vista previa de publicaciones",
      ],
    },
    {
      name: "Premium",
      price: 999.99,
      plan_id: "P-40D70369DE338721ENJYPMEQ",
      features: [
        "Publicaciones ilimitadas de ofertas",
        "Publicaciones ilimitadas de eventos",
        "Publicaciones ilimitadas de viajes",
        "Publicaciones ilimitadas de autos",
        "3 ofertas y 3 eventos destacados por mes",
        "Local en Pedidos (comida o súper)",
        "Vista previa de publicaciones",
      ],
    },
  ],
};

// Componente para iconos de categoría
const CategoryIcon = ({ category, sx }) => {
  switch (category) {
    case "destacadas":
      return <StarIcon sx={{ color: "warning.main", ...sx }} />;
    case "ofertas":
      return <LocalOfferIcon sx={{ color: "success.main", ...sx }} />;
    case "eventos":
      return <EventIcon sx={{ color: "primary.main", ...sx }} />;
    case "viajes":
      return <FlightIcon sx={{ color: "info.main", ...sx }} />;
    case "autos":
      return <DirectionsCarIcon sx={{ color: "warning.main", ...sx }} />;
    default:
      return <CheckCircleOutlineIcon color="success" sx={sx} />;
  }
};
const inferCategoryFromFeature = (featureText = "") => {
  const normalized = featureText.toLowerCase();

  if (normalized.includes("destacad")) return "destacadas";
  if (normalized.includes("ofertas")) return "ofertas";
  if (normalized.includes("eventos")) return "eventos";
  if (normalized.includes("viajes")) return "viajes";
  if (normalized.includes("autos")) return "autos";

  return "general";
};
const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      try {
        const session = await getSession();
        setUser(session);
      } catch (error) {
        console.error("Error getting session:", error);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  return { user, loading };
};

// Componente de selección de plan
const PlanSelection = ({
  selectedPlan,
  setSelectedPlan,
  billingInterval,
  setBillingInterval,
  hadFreePlan,
}) => {
  const visiblePlans = plans[billingInterval].filter(
    (plan) => !plan.isFreeTrial || !hadFreePlan
  );
  // 4 planes (con el gratis) van 4 por fila; 3 planes (sin el gratis) siguen
  // ocupando el ancho completo repartido en 3, en vez de dejar el cuarto
  // (Premium) solo en la fila de abajo. Con 4 en fila el gutter y el padding
  // interno de cada card se achican un poco para que no queden apretadas.
  const isFourUp = visiblePlans.length >= 4;
  const planColumnWidth = 12 / visiblePlans.length;
  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
        <ToggleButtonGroup
          value={billingInterval}
          exclusive
          onChange={(_, newInterval) =>
            newInterval && setBillingInterval(newInterval)
          }
          aria-label="intervalo de facturación"
        >
          <ToggleButton value="mensual"><T>Mensual</T></ToggleButton>
          <ToggleButton value="anual">
            <T>Anual</T>
            <Typography
              component="span"
              sx={{
                ml: 1,
                px: 1,
                py: 0.5,
                bgcolor: "success.light",
                color: "success.contrastText",
                borderRadius: "pill",
                fontSize: "0.75rem",
              }}
            >
              <T>Ahorra 20%</T>
            </Typography>
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Grid container spacing={isFourUp ? 2 : 3}>
        {visiblePlans.map((plan) => (
          <Grid
            item
            xs={12}
            sm={6}
            md={planColumnWidth}
            key={`${billingInterval}-${plan.name}`}
          >
            <Card
              onClick={() => setSelectedPlan(plan)}
              sx={{
                cursor: "pointer",
                height: "100%",
                transition: "all 0.2s",
                border: selectedPlan?.name === plan.name ? 2 : 1,
                borderColor:
                  selectedPlan?.name === plan.name
                    ? "primary.main"
                    : plan.isFreeTrial
                    ? "success.main"
                    : "divider",
                "&:hover": {
                  borderColor: "primary.main",
                  boxShadow: 2,
                },
              }}
            >
              <CardContent sx={{ p: isFourUp ? { xs: 2, md: 2.25 } : 2 }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontSize: isFourUp ? "1.05rem" : undefined }}
                >
                  <T>Plan</T>{" "}
                  <T>{plan.isFreeTrial ? "Gratis 1 mes" : plan.name}</T>
                </Typography>
                {plan.isFreeTrial ? (
                  <Typography
                    variant="h4"
                    component="div"
                    gutterBottom
                    sx={{ fontSize: isFourUp ? "1.65rem" : undefined }}
                  >
                    <T>Gratis</T>
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.secondary"
                      sx={{ ml: 1 }}
                    >
                      /<T>30 días</T>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <T>Luego</T> ${plan.priceAfterTrial}/<T>mes</T>
                    </Typography>
                  </Typography>
                ) : (
                  <Typography
                    variant="h4"
                    component="div"
                    gutterBottom
                    sx={{ fontSize: isFourUp ? "1.65rem" : undefined }}
                  >
                    ${plan.price}
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.secondary"
                      sx={{ ml: 1 }}
                    >
                      /{billingInterval === "mensual" ? <T>mes</T> : <T>año</T>}
                    </Typography>
                  </Typography>
                )}
                <Stack spacing={isFourUp ? 1.1 : 1.5} sx={{ mt: 2 }}>
                  {plan.features.map((featureText) => {
                    const category = inferCategoryFromFeature(featureText);

                    return (
                      <Box
                        key={`${plan.name}-${featureText}`}
                        sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
                      >
                        <CategoryIcon
                          category={category}
                          sx={
                            isFourUp
                              ? { fontSize: "1.1rem", mt: "2px" }
                              : undefined
                          }
                        />
                        <Typography
                          variant="body2"
                          sx={{ fontSize: isFourUp ? "0.8rem" : undefined }}
                        >
                          <T>{featureText}</T>
                        </Typography>
                      </Box>
                    );
                  })}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

// Activación de la prueba gratuita: no pasa por PayPal. La activa la Cloud
// Function claimFreeTrial (functions/subscriptions.js), una sola vez por
// cuenta: las reglas no dejan escribir users/{uid}.subscription desde el
// cliente. Al vencer, isFreePlanExpired (SubscriptionContext) la detecta y
// esta misma pantalla vuelve a mostrar los planes pagos (ver hadFreePlan).
const FreeTrialActivation = ({ selectedPlan, onActivated }) => {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const handleActivate = async () => {
    if (!user) {
      setError(<T>Usuario no autenticado</T>);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await functions.httpsCallable("claimFreeTrial")();
      onActivated(result.data.subscription);
    } catch (err) {
      console.error("Error al activar la prueba gratuita:", err);
      setError(
        err.code === "functions/already-exists"
          ? <T>Ya usaste tu mes gratis.</T>
          : <T>Error al activar tu mes gratis. Por favor, intenta nuevamente.</T>
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mb: 10 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
        }}
      >
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3, width: "100%" }}>
            {error}
          </Alert>
        )}

        <Alert severity="success" sx={{ mb: 3, width: "100%" }}>
          <T>Tu mes gratis no pide tarjeta. Al terminar, para seguir publicando
          elegís el plan Básico</T> (${selectedPlan.priceAfterTrial}/<T>mes</T>)
          <T>u otro plan superior.</T>
        </Alert>

        <Button
          variant="contained"
          color="success"
          size="large"
          onClick={handleActivate}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
        >
          <T>Comenzar mi mes gratis</T>
        </Button>
      </Box>
    </Container>
  );
};

// Componente de método de pago
const PaymentMethod = ({
  selectedPlan,
  billingInterval,
  onPaymentComplete,
}) => {
  // Agregamos billingInterval como prop
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paypalKey, setPaypalKey] = useState(0);
  const { user } = useAuth(); // Obtenemos user del hook useAuth

  useEffect(() => {
    setPaypalKey((prev) => prev + 1);
  }, [selectedPlan]);

  const paypalOptions = {
    // Misma app de PayPal que el resto de los pagos: los planes de arriba se
    // crearon ahí, y con el client-id viejo PayPal responde 404 al crearlos.
    "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID,
    currency: "USD",
    intent: "subscription",
    vault: true,
    "data-sdk-integration-source": "button-factory",
  };

  const handleCreateSubscription = async (data, actions) => {
    try {
      setLoading(true);

      if (!selectedPlan?.plan_id) {
        throw new Error("Plan ID no disponible");
      }

      return actions.subscription.create({
        plan_id: selectedPlan.plan_id,
        application_context: {
          shipping_preference: "NO_SHIPPING",
          user_action: "SUBSCRIBE_NOW",
          brand_name: "Notiline",
          locale: "es-ES",
        },
      });
    } catch (err) {
      console.error("Error al crear suscripción:", err);
      setError(<T>Error al crear la suscripción: {err.message}</T>);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (data, actions) => {
    if (!user) {
      setError(<T>Usuario no autenticado</T>);
      return;
    }

    try {
      setLoading(true);
      // La suscripción la valida y la guarda la Cloud Function
      // activateCompanySubscription contra la API de PayPal (el cliente ya
      // no puede escribir users/{uid}.subscription, ver firestore.rules).
      const result = await functions.httpsCallable("activateCompanySubscription")({
        subscriptionId: data.subscriptionID,
      });

      onPaymentComplete(result.data.subscription);
    } catch (err) {
      setError(<T>Error al procesar el pago. Por favor, contacte a soporte.</T>);
      console.error("Error detallado:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mb: 10 }}>
      <PayPalScriptProvider options={paypalOptions}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
          }}
        >
          {error && (
            <Alert
              severity="error"
              onClose={() => setError(null)}
              sx={{ mb: 3, width: "100%" }}
            >
              {error}
            </Alert>
          )}

          <Box sx={{ width: "100%", mb: 3 }}>
            <Box
              sx={{
                p: 3,
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                backgroundColor: "background.paper",
                position: "relative",
                display: "flex",
                justifyContent: "center",
                "& .paypal-buttons": {
                  maxWidth: "500px",
                  minWidth: "250px",
                  width: "100% !important",
                },
              }}
            >
              {loading && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "rgba(255, 255, 255, 0.7)",
                    zIndex: 1,
                  }}
                >
                  <CircularProgress />
                </Box>
              )}

              {/* Añadimos key al PayPalButtons para forzar su reinicio */}
              <PayPalSurface>
                <PayPalButtons
                  key={paypalKey}
                  style={{
                    shape: "rect",
                    color: "blue",
                    layout: "vertical",
                    label: "subscribe",
                  }}
                  createSubscription={handleCreateSubscription}
                  onApprove={handleApprove}
                  onError={(err) => setError(<T>Ocurrió un error con PayPal.</T>)}
                  onCancel={() => {
                    setLoading(false);
                    setError(
                      <T>
                        El pago no se completó. Si tu cuenta de PayPal no tiene
                        saldo suficiente, verifica tu método de pago o intenta
                        con otra tarjeta.
                      </T>
                    );
                  }}
                />
              </PayPalSurface>
            </Box>

            <Alert severity="info" sx={{ mt: 3, width: "100%" }}>
              <T>No necesitas tener una cuenta de PayPal - puedes pagar con tarjeta</T>
            </Alert>
          </Box>

          <Alert severity="success" icon={<LockIcon />} sx={{ width: "100%" }}>
            <T>Pago seguro procesado por PayPal</T>
          </Alert>
        </Box>
      </PayPalScriptProvider>
    </Container>
  );
};

const SubscriptionStatus = ({ subscriptionInfo, onCancelSubscription }) => {
  const [currentStatus, setCurrentStatus] = useState(subscriptionInfo.status);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { user } = useAuth();

  const handleCancelSubscription = async () => {
    if (!user?.uid) return;
    setIsUpdating(true);
    setError(null);

    try {
      // El servidor corta los cobros en PayPal y guarda hasta cuándo está pagado
      // el plan (paidUntil), que el cliente no puede escribir.
      const result = await functions.httpsCallable("cancelCompanySubscription")();

      setCurrentStatus("CANCELLED");
      onCancelSubscription && onCancelSubscription(result.data.subscription);

      setShowConfirmation(false);
    } catch (error) {
      console.error("Error canceling subscription:", error);
      // Estos dos errores traen del servidor el motivo y qué hacer.
      const explained = ["functions/not-found", "functions/unavailable"].includes(error.code);
      setError(
        explained
          ? <T text={error.message} />
          : <T>Error al cancelar la suscripción. Por favor intente nuevamente.</T>
      );
    } finally {
      setIsUpdating(false);
    }
  };

  // Helper functions remain the same...
  const getStatusDisplay = (status) => {
    const statusMap = {
      ACTIVE: { text: <T>Activa</T>, color: "success.main" },
      EXPIRED: { text: <T>Expirada</T>, color: "error.main" },
      CANCELLED: { text: <T>Cancelada</T>, color: "error.main" },
      SUSPENDED: { text: <T>Suspendida</T>, color: "warning.main" },
    };
    return statusMap[status] || { text: status, color: "text.primary" };
  };

  const getDaysRemaining = () => {
    if (!subscriptionInfo.nextBillingDate) return null;
    const nextBilling = new Date(subscriptionInfo.nextBillingDate);
    const now = new Date();
    const diffTime = nextBilling - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const statusDisplay = getStatusDisplay(currentStatus);
  const daysRemaining = getDaysRemaining();

  return (
    <Box sx={{ maxWidth: 600, mx: "auto", p: 3 }}>
      <Card>
        <CardContent>
          <Stack spacing={3}>
            <Box sx={{ textAlign: "center" }}>
              <CheckCircleOutlineIcon
                sx={{ fontSize: 64, color: statusDisplay.color, mb: 2 }}
              />
              <Typography variant="h5" gutterBottom>
                <T>Suscripción</T> {statusDisplay.text}
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle1" gutterBottom>
                <T>Detalles de la suscripción:</T>
              </Typography>
              <Stack spacing={1}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography><T>Plan:</T></Typography>
                  <Typography>{subscriptionInfo.planName}</Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography><T>Precio:</T></Typography>
                  <Typography>
                    ${subscriptionInfo.price}/{subscriptionInfo.interval}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography><T>Estado:</T></Typography>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography color={statusDisplay.color}>
                      {statusDisplay.text}
                    </Typography>
                    {isUpdating && (
                      <CircularProgress size={16} sx={{ ml: 1 }} />
                    )}
                  </Box>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography><T>ID de Suscripción:</T></Typography>
                  <Typography>{subscriptionInfo.subscriptionId}</Typography>
                </Box>
                {currentStatus === "CANCELLED" && subscriptionInfo.paidUntil ? (
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography><T>Plan activo hasta:</T></Typography>
                  <Typography>{new Date(subscriptionInfo.paidUntil).toLocaleDateString()}</Typography>
                </Box>
                ) : (
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography><T>Próximo cobro:</T></Typography>
                  <Typography>
                    {subscriptionInfo.nextBillingDate ? (
                      <>
                        {new Date(
                          subscriptionInfo.nextBillingDate
                        ).toLocaleDateString()}
                        {daysRemaining > 0 && (
                          <Typography
                            component="span"
                            color="text.secondary"
                            sx={{ ml: 1 }}
                          >
                            (<T>{daysRemaining} días</T>)
                          </Typography>
                        )}
                      </>
                    ) : (
                      <T>No disponible</T>
                    )}
                  </Typography>
                </Box>
                )}
              </Stack>
            </Box>

            {currentStatus === "ACTIVE" && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                {!showConfirmation ? (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => setShowConfirmation(true)}
                    startIcon={<CancelIcon />}
                    disabled={isUpdating}
                  >
                    <T>Cancelar Suscripción</T>
                  </Button>
                ) : (
                  <Box sx={{ width: "100%", textAlign: "center" }}>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      {subscriptionInfo.subscriptionId
                        ? <T>Vamos a cancelar los cobros automáticos en PayPal. Vas a seguir con tu plan hasta el final del período que ya pagaste.</T>
                        : <T>Tu mes gratis va a terminar ahora y no podrás volver a usarlo.</T>}
                    </Alert>
                    <Stack direction="row" spacing={2} justifyContent="center">
                      <Button
                        variant="outlined"
                        onClick={() => setShowConfirmation(false)}
                      >
                        <T>No, mantener suscripción</T>
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        onClick={handleCancelSubscription}
                        disabled={isUpdating}
                        startIcon={
                          isUpdating ? (
                            <CircularProgress size={20} />
                          ) : (
                            <CancelIcon />
                          )
                        }
                      >
                        <T>Sí, cancelar suscripción</T>
                      </Button>
                    </Stack>
                  </Box>
                )}
              </Box>
            )}

            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

// Componente de éxito
const SubscriptionSuccess = ({ subscriptionInfo, onContinue }) => {
  return (
    <Stack
      spacing={3}
      alignItems="center"
      sx={{ py: 4, maxWidth: 600, mx: "auto" }}
    >
      <CheckCircleOutlineIcon sx={{ fontSize: 64, color: "success.main" }} />
      <Typography variant="h5"><T>¡Suscripción Exitosa!</T></Typography>
      <Typography variant="body1" color="text.secondary" align="center">
        <T>Gracias por suscribirte. Tu suscripción está activa y ya puedes comenzar
        a disfrutar de todos los beneficios.</T>
      </Typography>
      <Box sx={{ width: "100%" }}>
        <Card>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              <T>Detalles de tu suscripción:</T>
            </Typography>
            <Stack spacing={1}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography><T>ID de Suscripción:</T></Typography>
                <Typography>{subscriptionInfo.subscriptionId}</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography><T>Plan:</T></Typography>
                <Typography>{subscriptionInfo.planName}</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography><T>Estado:</T></Typography>
                <Typography color="success.main">
                  {subscriptionInfo.status}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Box>
      <Button
        variant="contained"
        size="large"
        onClick={onContinue}
        endIcon={<ArrowForwardIcon />}
      >
        <T>Continuar a mi cuenta</T>
      </Button>
    </Stack>
  );
};

const SubscriptionCheckout = () => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingInterval, setBillingInterval] = useState("mensual");
  const [subscriptionComplete, setSubscriptionComplete] = useState(false);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [consumerLoading, setConsumerLoading] = useState(false);
  const navigate = useNavigate();

  // Get subscription context and auth
  const {
    subscription,
    loading: subscriptionLoading,
    setSubscription,
    isSubscriptionActive,
  } = useSubscriptionContext();
  const { user, loading: authLoading } = useAuth();

  // Show loading state while either auth or subscription is loading
  if (authLoading || subscriptionLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    navigate("/login");
    return null;
  }

  // Se chequea antes que isSubscriptionActive: apenas se acepta el plan
  // gratis (o se completa un pago), setSubscription ya deja el estado en
  // ACTIVE en este mismo render, así que si isSubscriptionActive fuera
  // primero, esta pantalla de éxito (con el botón a Home) nunca se vería.
  if (subscriptionComplete) {
    return (
      <Container maxWidth={false} disableGutters sx={pageContentSx}>
        <PageHeader title={<T>Suscripción Completada</T>} />
        <Box>
          <SubscriptionSuccess
            subscriptionInfo={subscriptionDetails}
            onContinue={() => navigate("/home")}
          />
        </Box>
      </Container>
    );
  }

  if (isSubscriptionActive) {
    return (
      <Container maxWidth={false} disableGutters sx={pageContentSx}>
        <PageHeader title={<T>Suscripciones</T>} />
        <Box>
          <SubscriptionStatus
            subscriptionInfo={subscription}
            onCancelSubscription={(cancelledSubscription) => {
              setSubscription(cancelledSubscription);
              navigate("/home");
            }}
          />
        </Box>
      </Container>
    );
  }

  // El plan Free (1 mes, ver plans.mensual) solo se ofrece una vez por
  // cuenta. Si esta cuenta ya tuvo uno (y venció, porque si estuviera activo
  // isSubscriptionActive ya la habría mandado a SubscriptionStatus más
  // arriba), PlanSelection lo oculta y avisamos que toca elegir un plan pago.
  const hadFreePlan = subscription?.planName === "Free";
  const paidPlansBannerText = hadFreePlan
    ? "Tu mes de membresía gratuita terminó. Elige el plan Básico u otro superior para seguir publicando."
    : "Elige un plan para comenzar a publicar en Notiline. ¿Primera vez? Probá gratis por 1 mes.";

  const handleContinueAsConsumer = async () => {
    setConsumerLoading(true);
    try {
      await updateDoc(doc(db, "users", user.uid), {
        userType: "consumidor",
      });
      navigate("/home");
    } catch (error) {
      console.error("Error al continuar como consumidor:", error);
    } finally {
      setConsumerLoading(false);
    }
  };

  return (
    <Container maxWidth={false} disableGutters sx={pageContentSx}>
      <PageHeader title={<T>Suscripciones</T>} />
      <CssBaseline />
      <Grid container spacing={3} sx={{ mt: 3 }}>
        <Grid item xs={12}>
          <Alert severity="info">
            <T>{paidPlansBannerText}</T>
          </Alert>
        </Grid>
        <Grid item xs={12}>
          <PlanSelection
            selectedPlan={selectedPlan}
            setSelectedPlan={setSelectedPlan}
            billingInterval={billingInterval}
            setBillingInterval={setBillingInterval}
            hadFreePlan={hadFreePlan}
          />
        </Grid>
        {selectedPlan && (
          <Grid item xs={12}>
            {selectedPlan.isFreeTrial ? (
              <FreeTrialActivation
                selectedPlan={selectedPlan}
                onActivated={(details) => {
                  // A diferencia del pago con PayPal, acá no hace falta la
                  // pantalla intermedia de éxito: se activa al toque y te
                  // deja directo en Home para que arranques a publicar.
                  setSubscription(details);
                  navigate("/home");
                }}
              />
            ) : (
              <PaymentMethod
                selectedPlan={selectedPlan}
                billingInterval={billingInterval}
                onPaymentComplete={(details) => {
                  // handleApprove ya guardó en Firestore; solo actualizamos estado local
                  setSubscription(details);
                  setSubscriptionDetails(details);
                  setSubscriptionComplete(true);
                }}
              />
            )}
          </Grid>
        )}
        <Grid item xs={12}>
          <Stack spacing={2} alignItems="center" sx={{ mt: 2, mb: 6 }}>
            <Typography color="text.secondary">
              <T>¿Solo querés navegar y comprar, no publicar?</T>
            </Typography>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              onClick={handleContinueAsConsumer}
              disabled={consumerLoading}
              startIcon={
                consumerLoading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : null
              }
            >
              <T>Continuar como consumidor</T>
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Container>
  );
};

export default SubscriptionCheckout;
